import clsx from 'clsx';
import { AlertCircle, Check, CheckCheck, Clock, Copy, FileText, Lock, Reply, Users } from 'lucide-react';
import { MouseEvent, ReactNode, TouchEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ImageViewer } from '../../components/ImageViewer';
import { LottieSticker } from '../../components/LottieSticker';
import { Empty, Spinner } from '../../components/ui';
import { fetchBlobUrl } from '../../lib/api';
import { dateOf, timeOf } from '../../lib/format';
import { parseWhatsAppFormatting } from '../../lib/whatsapp-format';
import { Message } from '../../lib/types';
import { useEscapeLayer } from '../../lib/escape';

/** Rótulo curto de uma mensagem (citação, menu): o texto ou o tipo. */
export function messageSnippet(m: Message): string {
  if (m.body?.trim()) return m.body.trim();
  const labels: Record<string, string> = { image: 'Foto', video: 'Vídeo', audio: 'Áudio', document: 'Documento', sticker: 'Figurinha', location: 'Localização', contact: 'Contato' };
  return labels[m.type] ?? 'Mensagem';
}

const quotedAuthor = (m: Message) => (m.direction === 'inbound' ? 'Cliente' : (m.senderName ?? (m.origin === 'device' ? 'Pelo celular' : 'Você')));

/** Reação mais recente de cada lado (cliente / nós) por mensagem; emoji vazio = reação retirada. */
function reactionsByTarget(messages: Message[], known: Map<string, Message>) {
  const latest = new Map<string, Message>();
  for (const m of messages) {
    if (m.type !== 'reaction' || !m.quotedExternalId || !known.has(m.quotedExternalId)) continue;
    const key = `${m.quotedExternalId}|${m.direction}`;
    const prev = latest.get(key);
    if (!prev || prev.occurredAt <= m.occurredAt) latest.set(key, m);
  }
  const out = new Map<string, Array<{ emoji: string; fromCustomer: boolean }>>();
  for (const r of latest.values()) {
    if (!r.body) continue;
    const list = out.get(r.quotedExternalId!) ?? [];
    list.push({ emoji: r.body, fromCustomer: r.direction === 'inbound' });
    out.set(r.quotedExternalId!, list);
  }
  return out;
}

interface Menu {
  x: number;
  y: number;
  m: Message;
}

/**
 * Quem está vendo e quem trabalha no atendimento: notas de outro operador (ajudante ou
 * responsável) e mensagens de quem ajuda ganham destaque, para não se perderem entre as próprias.
 */
export interface Viewer {
  userId: string;
  ownerId: string | null;
  /** Quem está ajudando agora. */
  helperIds: string[];
}

type Role = 'mine' | 'owner' | 'helper' | 'other' | null;

function roleOf(m: Message, viewer: Viewer | undefined): Role {
  if (!viewer || !m.sentByUserId || m.direction === 'inbound') return null;
  if (m.sentByUserId === viewer.userId) return 'mine';
  if (viewer.helperIds.includes(m.sentByUserId)) return 'helper';
  if (m.sentByUserId === viewer.ownerId) return 'owner';
  return 'other';
}

const ROLE_LABEL: Partial<Record<NonNullable<Role>, string>> = { helper: 'ajudando', owner: 'responsável' };

export function MessageList({
  messages,
  loading,
  attendanceId,
  viewer,
  onReply,
}: {
  messages: Message[];
  loading: boolean;
  attendanceId: string;
  viewer?: Viewer;
  /** Responder a uma mensagem (botão direito ou segurar no celular); ausente = sem a opção. */
  onReply?: (m: Message) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const [menu, setMenu] = useState<Menu | null>(null);

  // Fica no fim enquanto o operador não rolar para cima.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  useEscapeLayer(menu !== null, () => setMenu(null));

  const known = useMemo(() => new Map(messages.filter((m) => m.externalId && m.type !== 'reaction').map((m) => [m.externalId!, m])), [messages]);
  const reactions = useMemo(() => reactionsByTarget(messages, known), [messages, known]);

  if (loading) return <Empty><Spinner /></Empty>;
  if (messages.length === 0) return <Empty>Sem mensagens.</Empty>;

  const canMenu = (m: Message) => m.direction !== 'internal' && m.type !== 'reaction';
  const openMenu = (m: Message, x: number, y: number) => setMenu({ m, x: Math.min(x, window.innerWidth - 180), y: Math.min(y, window.innerHeight - 100) });

  let lastDay = '';
  return (
    <div
      ref={ref}
      className="h-full overflow-y-auto bg-bg px-4 py-3"
      onScroll={(e) => {
        const el = e.currentTarget;
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      }}
    >
      {messages.map((m) => {
        // Reação a uma mensagem da lista: aparece nela, não como mensagem solta.
        if (m.type === 'reaction' && m.quotedExternalId && known.has(m.quotedExternalId)) return null;
        const day = dateOf(m.occurredAt);
        const separator = day !== lastDay;
        lastDay = day;
        const quoted = m.type !== 'reaction' && m.quotedExternalId ? (known.get(m.quotedExternalId) ?? 'missing') : null;
        return (
          <div key={m.id} id={`msg-${m.id}`}>
            {separator && (
              <div className="my-3 text-center">
                <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-fg-soft">{day}</span>
              </div>
            )}
            <Bubble
              m={m}
              role={roleOf(m, viewer)}
              otherAttendance={Boolean(m.attendanceId && m.attendanceId !== attendanceId)}
              quoted={quoted}
              reactions={m.externalId ? (reactions.get(m.externalId) ?? []) : []}
              onMenu={canMenu(m) ? (x, y) => openMenu(m, x, y) : undefined}
            />
          </div>
        );
      })}
      {menu && (
        <div className="fixed inset-0 z-50" onClick={() => setMenu(null)} onContextMenu={(e) => (e.preventDefault(), setMenu(null))}>
          <div
            className="absolute w-44 overflow-hidden rounded-md border border-border bg-panel py-1 text-sm shadow-lg"
            style={{ left: menu.x, top: menu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            {onReply && menu.m.externalId && (
              <button
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
                onClick={() => {
                  onReply(menu.m);
                  setMenu(null);
                }}
              >
                <Reply className="size-4" /> Responder
              </button>
            )}
            {menu.m.body && (
              <button
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
                onClick={() => {
                  void navigator.clipboard?.writeText(menu.m.body ?? '');
                  setMenu(null);
                }}
              >
                <Copy className="size-4" /> Copiar texto
              </button>
            )}
            {!(onReply && menu.m.externalId) && !menu.m.body && <div className="px-3 py-1.5 text-fg-soft">Sem ações</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/** Leva até a mensagem citada e a destaca por um instante. */
function goTo(m: Message) {
  const el = document.getElementById(`msg-${m.id}`);
  if (!el) return;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  el.classList.add('animate-pulse');
  setTimeout(() => el.classList.remove('animate-pulse'), 1600);
}

function Bubble({
  m,
  role,
  otherAttendance,
  quoted,
  reactions,
  onMenu,
}: {
  m: Message;
  role: Role;
  otherAttendance: boolean;
  quoted: Message | 'missing' | null;
  reactions: Array<{ emoji: string; fromCustomer: boolean }>;
  onMenu?: (x: number, y: number) => void;
}) {
  const press = useRef<number | null>(null);
  // Celular: segurar o dedo abre o menu (não há botão direito).
  const touch = onMenu
    ? {
        onTouchStart: (e: TouchEvent) => {
          const t = e.touches[0];
          press.current = window.setTimeout(() => onMenu(t.clientX, t.clientY), 500);
        },
        onTouchEnd: () => press.current && clearTimeout(press.current),
        onTouchMove: () => press.current && clearTimeout(press.current),
        onContextMenu: (e: MouseEvent) => {
          e.preventDefault();
          onMenu(e.clientX, e.clientY);
        },
      }
    : {};
  if (m.direction === 'internal') {
    // Nota de outro operador do atendimento: azul e com o papel dele; as próprias (e as automáticas) seguem amarelas.
    const team = role === 'helper' || role === 'owner' || role === 'other';
    return (
      <div className="my-2 flex justify-center">
        <div className={clsx('max-w-[90%] rounded-lg border px-3 py-2 text-sm md:max-w-[80%]', team ? 'border-team/50 bg-bubble-team border-l-4 border-l-team' : 'border-warn/40 bg-bubble-note')}>
          <div className={clsx('mb-0.5 flex flex-wrap items-center gap-1 text-[11px] font-medium', team ? 'text-team' : 'text-warn')}>
            {team ? <Users className="size-3" /> : <Lock className="size-3" />}
            {team ? `Nota de ${m.senderName ?? 'outro operador'}` : role === 'mine' ? 'Sua nota interna' : `Nota interna · ${m.senderName ?? 'Operador'}`}
            {ROLE_LABEL[role!] && <RoleTag label={ROLE_LABEL[role!]!} />}· {timeOf(m.occurredAt)}
          </div>
          <div className="whitespace-pre-wrap break-words"><FormattedText text={m.body ?? ''} /></div>
          {m.media && (
            <div className="mt-1">
              <Media m={m} />
            </div>
          )}
        </div>
      </div>
    );
  }
  const inbound = m.direction === 'inbound';
  const author = inbound ? null : m.origin === 'device' ? 'Pelo celular' : (m.senderName ?? (m.sentByUserId ? null : 'Mensagem automática'));
  return (
    <div className={clsx('flex', reactions.length ? 'mt-1 mb-4' : 'my-1', inbound ? 'justify-start' : 'justify-end', otherAttendance && 'opacity-60')}>
      <div
        className={clsx(
          'relative max-w-[85%] rounded-lg px-3 py-1.5 text-sm shadow-sm select-text md:max-w-[70%]',
          inbound ? 'bg-bubble-in' : 'bg-bubble-out',
          // Quem ajuda escreve ao cliente com o próprio nome: a borda azul mostra que não foi o responsável.
          role === 'helper' && 'border-l-4 border-team',
        )}
        {...touch}
      >
        {author && (
          <div className={clsx('mb-0.5 flex items-center gap-1 text-[11px] font-medium', role === 'helper' ? 'text-team' : 'text-fg-soft')}>
            {author}
            {ROLE_LABEL[role!] && <RoleTag label={ROLE_LABEL[role!]!} />}
          </div>
        )}
        {quoted && (
          <button
            type="button"
            disabled={quoted === 'missing'}
            onClick={() => quoted !== 'missing' && goTo(quoted)}
            className="mb-1 block w-full rounded border-l-4 border-accent bg-black/10 px-2 py-1 text-left text-xs disabled:cursor-default"
            title={quoted === 'missing' ? undefined : 'Ir para a mensagem'}
          >
            {quoted === 'missing' ? (
              <span className="text-fg-soft italic">Resposta a uma mensagem anterior</span>
            ) : (
              <>
                <span className="block font-medium text-accent">{quotedAuthor(quoted)}</span>
                <span className="line-clamp-2 text-fg-soft">{messageSnippet(quoted)}</span>
              </>
            )}
          </button>
        )}
        {m.type === 'reaction' ? (
          <span className="text-fg-soft">{m.body ? `reagiu com ${m.body} a uma mensagem anterior` : 'retirou a reação de uma mensagem anterior'}</span>
        ) : (
          <>
            {m.media && <Media m={m} />}
            {m.body && <div className="whitespace-pre-wrap break-words"><FormattedText text={m.body} /></div>}
            {!m.body && !m.media && <span className="text-fg-soft italic">({m.type})</span>}
            {m.editedAt && !m.editReadable && (
              <div className="mt-0.5 text-[11px] text-warn">O cliente editou esta mensagem no celular; o texto novo não chegou.</div>
            )}
          </>
        )}
        <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-fg-soft">
          {m.editedAt && <span title={`Editada em ${dateOf(m.editedAt)} ${timeOf(m.editedAt)}`}>editada ·</span>}
          {timeOf(m.occurredAt)}
          {!inbound && <StatusIcon status={m.status} error={m.error} />}
        </div>
        {reactions.length > 0 && (
          <div className={clsx('absolute -bottom-3.5 flex gap-0.5 rounded-full border border-border bg-panel px-1.5 py-0.5 text-xs shadow-sm', inbound ? 'left-2' : 'right-2')}>
            {reactions.map((r) => (
              <span key={`${r.fromCustomer}`} title={r.fromCustomer ? 'Reação do cliente' : 'Nossa reação (pelo celular)'}>
                {r.emoji}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Texto com a formatação do WhatsApp (*negrito*, _itálico_, ~riscado~, `código`), como o cliente vê. */
function FormattedText({ text }: { text: string }) {
  const parts = useMemo(() => parseWhatsAppFormatting(text), [text]);
  return parts.map((p, i) => {
    let node: ReactNode = p.text;
    if (p.code) node = <code className="rounded bg-muted px-1 font-mono text-[0.9em]">{node}</code>;
    if (p.strike) node = <s>{node}</s>;
    if (p.italic) node = <em>{node}</em>;
    if (p.bold) node = <strong className="font-semibold">{node}</strong>;
    return <span key={i}>{node}</span>;
  });
}

function RoleTag({ label }: { label: string }) {
  return <span className="rounded-full bg-team/15 px-1.5 text-[10px] leading-4 font-medium text-team">{label}</span>;
}

function StatusIcon({ status, error }: { status: string; error: string | null }) {
  switch (status) {
    case 'queued':
    case 'sending':
      return <Clock className="size-3" />;
    case 'sent':
      return <Check className="size-3" />;
    case 'delivered':
      return <CheckCheck className="size-3" />;
    case 'read':
      return <CheckCheck className="size-3 text-accent" />;
    case 'failed':
      return (
        <span title={error ?? 'Falha no envio'}>
          <AlertCircle className="size-3 text-danger" />
        </span>
      );
    default:
      return null;
  }
}

/** Transcrição automática do áudio (IA), recolhível quando é longa (gravação de ligação). */
function Transcript({ media }: { media: NonNullable<Message['media']> }) {
  const [open, setOpen] = useState(false);
  if (media.transcriptStatus === 'processing') {
    return (
      <div className="mb-1 flex items-center gap-1.5 text-[11px] text-fg-soft">
        <Spinner className="size-3" /> transcrevendo…
      </div>
    );
  }
  if (media.transcriptStatus !== 'done' || !media.transcript) return null;
  const long = media.transcript.length > 280;
  return (
    <div className="mb-1 max-w-md rounded-md bg-muted/60 px-2 py-1.5 text-xs">
      <div className="mb-0.5 text-[10px] font-medium tracking-wide text-fg-soft uppercase">Transcrição automática</div>
      <div className={clsx('whitespace-pre-wrap break-words', long && !open && 'line-clamp-4')}>{media.transcript}</div>
      {long && (
        <button type="button" className="mt-0.5 text-[11px] text-accent hover:underline" onClick={() => setOpen((v) => !v)}>
          {open ? 'mostrar menos' : 'mostrar tudo'}
        </button>
      )}
    </div>
  );
}

/** Mídia protegida: baixa com o token e mostra como blob:. */
function Media({ m }: { m: Message }) {
  const media = m.media!;
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [viewing, setViewing] = useState(false);
  const ready = media.downloadStatus === 'done';

  useEffect(() => {
    if (!ready) return;
    let revoked: string | null = null;
    let alive = true;
    fetchBlobUrl(`/messages/${m.id}/media`)
      .then((u) => {
        revoked = u;
        if (alive) setUrl(u);
        else URL.revokeObjectURL(u);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [m.id, ready]);

  if (m.type === 'sticker' && media.mimeType === 'application/lottie+json' && ready) return <LottieSticker path={`/messages/${m.id}/media`} className="mb-1 size-28" />;
  if (media.downloadStatus === 'recording') return <div className="flex items-center gap-2 py-1 text-xs text-fg-soft"><Spinner className="size-3" /> buscando a gravação no PBX…</div>;
  if (media.downloadStatus === 'failed' || failed) return <div className="text-xs text-danger">{(m.direction === 'internal' && media.downloadError) || 'Não foi possível baixar a mídia.'}</div>;
  if (!ready || !url) return <div className="flex items-center gap-2 py-1 text-xs text-fg-soft"><Spinner className="size-3" /> carregando mídia…</div>;

  const name = media.fileName ?? 'arquivo';
  switch (m.type) {
    case 'sticker':

      return <img src={url} alt="figurinha" className="mb-1 w-28" />;
    case 'image':
      return (
        <>
          <button type="button" className="mb-1 block cursor-zoom-in" onClick={() => setViewing(true)} title="Ampliar">
            <img src={url} alt={name} className="max-h-72 max-w-full rounded-md" />
          </button>
          {viewing && <ImageViewer url={url} name={name} caption={m.body} onClose={() => setViewing(false)} />}
        </>
      );
    case 'audio':
      return (
        <>
          <audio controls src={url} className="mb-1 w-64 max-w-full" />
          <Transcript media={media} />
        </>
      );
    case 'video':
      return <video controls src={url} className="mb-1 max-h-72 max-w-full rounded-md" />;
    default:
      return (
        <a href={url} download={name} className="mb-1 flex items-center gap-2 rounded-md bg-muted px-2 py-1.5 hover:underline">
          <FileText className="size-4 shrink-0" />
          <span className="truncate">{name}</span>
        </a>
      );
  }
}
